import React, { useState } from 'react';
import { shallowEqual } from 'react-redux';
import ClearIcon from '@mui/icons-material/Clear';
import LinearScaleIcon from '@mui/icons-material/LinearScale';
import WarningIcon from '@mui/icons-material/Warning';
import { Box, Button, Typography } from '@mui/material';

import { TooltipIconButton } from '@paragon/client-tools';
import { InlineNotification } from '@paragon/notification-tools';

import { RootState } from '../../store';
import { getReferenceLineLongNamesByGuidForActiveComponent } from '../../store/selectors/getReferenceLineLongNamesByGuidForActiveComponentSelector';
import { getReferenceLinesForActiveComponent } from '../../store/selectors/getReferenceLinesForActiveComponent';
import { getPickingObjectInfo } from '../../store/selectors/pickingSelectors';
import { propertiesShouldBeViewOnly } from '../../store/selectors/viewOnlyModeSelectors';
import { clearPickingObject } from '../../store/slices/applicationSlice';
import { useAppDispatch, useAppSelector } from '../hooks';
import { LabeledBox } from '../interface/subpanels/properties/LabeledBox';
import { ReferenceLineProperties } from '../interface/subpanels/properties/ReferenceLineProperties';

interface Props {
  label?: string;
  elevation?: number;
  lineGuid: string | null;

  startSelectLine: () => void;
}

export const ReferenceLinePicker = ({ label, elevation, lineGuid, startSelectLine }: Props) => {
  const dispatch = useAppDispatch();

  const { referenceLines, referenceLineInfo, isViewOnly } = useAppSelector(mapStateToProps, shallowEqual);
  const referenceLineLongName = useAppSelector((state) =>
    lineGuid == null ? null : (getReferenceLineLongNamesByGuidForActiveComponent(state)?.get(lineGuid) ?? null),
  );

  const [newLineGuid, setInsertingNewLineGuid] = useState<string | undefined>(undefined);

  const isCurrentlyPicking = !!referenceLineInfo;

  const referenceLine = referenceLines?.find((line) => line.guid === lineGuid);

  const hasLine = !!(lineGuid && referenceLine);

  const onInsertNewReferenceLine = () => {
    if (!newLineGuid || !referenceLine) throw new Error('Invalid state.');
    setInsertingNewLineGuid(undefined);
  };
  const onCancelNewReferenceLine = () => setInsertingNewLineGuid(undefined);

  return (
    <LabeledBox title={label ?? 'Reference Line'}>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', pl: 2, mt: 1 }}>
        {!hasLine && !isCurrentlyPicking && <WarningIcon color="error" />}

        <Typography color={!hasLine || !isCurrentlyPicking ? 'textSecondary' : 'textPrimary'} sx={{ py: 1 }}>
          {isCurrentlyPicking ? 'Choosing reference line...' : (referenceLineLongName ?? 'None Chosen')}
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        {referenceLineInfo ? (
          <TooltipIconButton title="Cancel" disabled={isViewOnly} onClick={() => void dispatch(clearPickingObject())}>
            <ClearIcon />
          </TooltipIconButton>
        ) : (
          <TooltipIconButton title="Select Existing" disabled={isViewOnly} onClick={startSelectLine}>
            <LinearScaleIcon />
          </TooltipIconButton>
        )}
      </Box>

      {referenceLine && (
        <ReferenceLineProperties elevation={elevation} compact sx={{ boxShadow: 'none' }} line={referenceLine} />
      )}

      {newLineGuid && (
        <InlineNotification type="info" sx={{ m: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <Typography sx={{ marginRight: 'auto' }}>Inserting reference line</Typography>
            <Button sx={{ m: 1 }} variant="outlined" color="secondary" onClick={onCancelNewReferenceLine}>
              Cancel
            </Button>
            <Button sx={{ m: 1 }} variant="contained" color="secondary" onClick={onInsertNewReferenceLine}>
              Insert
            </Button>
          </Box>
        </InlineNotification>
      )}
    </LabeledBox>
  );
};

const mapStateToProps = (state: RootState) => {
  const info = getPickingObjectInfo(state);
  const referenceLineInfo =
    info &&
    info.type === 'pickingReferenceLine' &&
    (info.targetInfo.type === 'memberCut' || info.targetInfo.type === 'memberLine')
      ? info.targetInfo
      : null;

  return {
    referenceLineInfo,
    referenceLines: getReferenceLinesForActiveComponent(state),
    isViewOnly: propertiesShouldBeViewOnly(state),
  };
};
